import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Users } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { SuppliersForm } from '../forms/SuppliersForm';
import SuppliersTable from '../suppliers/SuppliersTable';
import SupplierContactsTable from '../suppliers/SupplierContactsTable';
import SupplierDetailModal from './SupplierDetailModal';
import SuppliersBulkImportModal, {
  downloadSuppliersExcelTemplate,
} from './SuppliersBulkImportModal';
import SupplierContactsBulkImportModal, {
  downloadSupplierContactsExcelTemplate,
} from './SupplierContactsBulkImportModal';
import { useUIColors } from '../../hooks/useTheme';
import suppliersAPI from '../../services/suppliersAPI';
import supplierContactsAPI from '../../services/supplierContactsAPI';
import procurementCatalogsAPI from '../../services/procurementCatalogsAPI';
import { getActiveOptionsFromRows, toUiCatalogRow } from './procurementCatalogDefaults';
import { capitalizeWords, formatChileanRut, normalizeProperCaseText } from '../../utils/textUtils';

const EMPTY_SUPPLIER_FORM = {
  supplier_code: '',
  name: '',
  business_name: '',
  rut: '',
  address: '',
  supplier_type: '',
  status: 'Activo',
  payment_conditions: [],
  addresses: [],
  bank_accounts: [],
  contacts: [],
};

const EMPTY_CONTACT_FORM = {
  full_name: '',
  role: '',
  email: '',
  phone: '',
  supplier_id: '',
  status: 'Activo',
};

const syncSupplierContacts = async (supplierId, desiredContacts = [], existingContacts = []) => {
  const desired = (desiredContacts || []).filter((c) => String(c.full_name || '').trim());
  const existingById = new Map((existingContacts || []).map((c) => [c.id, c]));
  const keptIds = new Set();

  for (const contact of desired) {
    const payload = {
      full_name: capitalizeWords(String(contact.full_name || '').trim()),
      role: capitalizeWords(String(contact.role || '').trim()),
      email: String(contact.email || '').trim(),
      phone: String(contact.phone || '').trim(),
      status: String(contact.status || 'Activo').trim() || 'Activo',
      supplier_id: supplierId,
    };
    if (contact.id && existingById.has(contact.id)) {
      await supplierContactsAPI.update(contact.id, payload);
      keptIds.add(contact.id);
    } else {
      await supplierContactsAPI.create(payload);
    }
  }

  for (const existing of existingContacts || []) {
    if (!keptIds.has(existing.id)) {
      await supplierContactsAPI.delete(existing.id);
    }
  }
};

const PaintSuppliersWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();

  const [suppliers, setSuppliers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [suppliersError, setSuppliersError] = useState('');
  const [contactsError, setContactsError] = useState('');

  const [paymentConditionOptions, setPaymentConditionOptions] = useState([]);
  const [supplierTypeOptions, setSupplierTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState(EMPTY_SUPPLIER_FORM);
  const [supplierFormError, setSupplierFormError] = useState('');
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState(null);

  const [detailSupplier, setDetailSupplier] = useState(null);

  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [isImportingSuppliers, setIsImportingSuppliers] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportNotice, setBulkImportNotice] = useState('');

  const [showContactBulkImportModal, setShowContactBulkImportModal] = useState(false);
  const [isImportingContacts, setIsImportingContacts] = useState(false);
  const [contactBulkImportResult, setContactBulkImportResult] = useState(null);

  const [showContactForm, setShowContactForm] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_FORM);
  const [contactFormError, setContactFormError] = useState('');
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);

  const loadCatalogs = useCallback(async () => {
    try {
      const grouped = await procurementCatalogsAPI.getGrouped();
      setPaymentConditionOptions(
        getActiveOptionsFromRows((grouped?.payment_conditions || []).map(toUiCatalogRow))
      );
      setSupplierTypeOptions(
        getActiveOptionsFromRows((grouped?.supplier_types || []).map(toUiCatalogRow))
      );
      setCurrencyOptions(
        getActiveOptionsFromRows((grouped?.currencies || []).map(toUiCatalogRow), {
          valueFromCode: true,
        })
      );
    } catch (error) {
      console.error('Error loading procurement catalogs:', error);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    setSuppliersError('');
    try {
      const data = await suppliersAPI.getAll();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliersError(error?.message || 'No se pudieron cargar los proveedores.');
      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    setContactsError('');
    try {
      const data = await supplierContactsAPI.getAll();
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading supplier contacts:', error);
      setContactsError(error?.message || 'No se pudieron cargar los contactos.');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogs();
    loadSuppliers();
    loadContacts();
  }, [loadCatalogs, loadSuppliers, loadContacts]);

  const contactsBySupplierId = useMemo(() => {
    const map = new Map();
    contacts.forEach((contact) => {
      const sid = Number(contact.supplier_id || 0);
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid).push(contact);
    });
    return map;
  }, [contacts]);

  const detailContacts = detailSupplier
    ? contactsBySupplierId.get(Number(detailSupplier.id)) || []
    : [];

  const supplierSelectOptions = useMemo(
    () => [
      { value: '', label: 'Seleccionar proveedor' },
      ...suppliers.map((s) => ({
        value: String(s.id),
        label: `${s.supplier_code || ''} · ${s.name}`.trim(),
      })),
    ],
    [suppliers]
  );

  const openCreateSupplier = () => {
    setIsEditingSupplier(false);
    setEditingSupplierId(null);
    setSupplierFormError('');
    setSupplierFormData({ ...EMPTY_SUPPLIER_FORM });
    setShowSupplierForm(true);
  };

  const openEditSupplier = (supplier) => {
    if (!supplier) return;
    const related = contactsBySupplierId.get(Number(supplier.id)) || [];
    const existingAddresses = Array.isArray(supplier.addresses) ? supplier.addresses : [];
    const legacyAddress =
      existingAddresses.length === 0 && supplier.address
        ? [
            {
              address_type: 'Facturación',
              address: supplier.address || '',
              commune: '',
              city: '',
              region: '',
              country: 'Chile',
              notes: '',
            },
          ]
        : [];
    setIsEditingSupplier(true);
    setEditingSupplierId(supplier.id);
    setSupplierFormError('');
    setSupplierFormData({
      supplier_code: supplier.supplier_code || '',
      name: supplier.name || '',
      business_name: supplier.business_name || '',
      rut: supplier.rut || '',
      address: supplier.address || '',
      supplier_type: supplier.supplier_type || '',
      status: supplier.status || 'Activo',
      payment_conditions: Array.isArray(supplier.payment_conditions)
        ? [...supplier.payment_conditions]
        : [],
      addresses: (existingAddresses.length ? existingAddresses : legacyAddress).map((row) => ({
        address_type: row.address_type || 'Facturación',
        address: row.address || '',
        commune: row.commune || '',
        city: row.city || '',
        region: row.region || '',
        country: row.country || 'Chile',
        notes: row.notes || '',
      })),
      bank_accounts: (Array.isArray(supplier.bank_accounts) ? supplier.bank_accounts : []).map(
        (row) => ({
          bank_name: row.bank_name || '',
          account_type: row.account_type || 'Corriente',
          account_number: row.account_number || '',
          account_holder: row.account_holder || '',
          holder_rut: row.holder_rut || '',
          currency: row.currency || 'CLP',
          status: row.status || 'Activo',
          notes: row.notes || '',
        })
      ),
      contacts: related.map((c) => ({
        id: c.id,
        full_name: c.full_name || '',
        role: c.role || '',
        email: c.email || '',
        phone: c.phone || '',
        status: c.status || 'Activo',
      })),
    });
    setDetailSupplier(null);
    setShowSupplierForm(true);
  };

  const handleSaveSupplier = async () => {
    const name = capitalizeWords(String(supplierFormData.name || '').trim());
    if (!name) {
      setSupplierFormError('El nombre es obligatorio.');
      return;
    }

    const addresses = (Array.isArray(supplierFormData.addresses) ? supplierFormData.addresses : [])
      .map((row) => ({
        address_type: String(row.address_type || 'Facturación').trim() || 'Facturación',
        address: normalizeProperCaseText(String(row.address || '').trim()),
        commune: capitalizeWords(String(row.commune || '').trim()),
        city: capitalizeWords(String(row.city || '').trim()),
        region: capitalizeWords(String(row.region || '').trim()),
        country: capitalizeWords(String(row.country || '').trim()) || 'Chile',
        notes: String(row.notes || '').trim(),
      }))
      .filter((row) => row.address);

    const bankAccounts = (Array.isArray(supplierFormData.bank_accounts)
      ? supplierFormData.bank_accounts
      : []
    )
      .map((row) => ({
        bank_name: capitalizeWords(String(row.bank_name || '').trim()),
        account_type: String(row.account_type || 'Corriente').trim() || 'Corriente',
        account_number: String(row.account_number || '').trim(),
        account_holder: capitalizeWords(String(row.account_holder || '').trim()),
        holder_rut: formatChileanRut(String(row.holder_rut || '').trim()),
        currency: String(row.currency || 'CLP').trim().toUpperCase() || 'CLP',
        status: String(row.status || 'Activo').trim() || 'Activo',
        notes: String(row.notes || '').trim(),
      }))
      .filter((row) => row.bank_name || row.account_number);

    const payload = {
      name,
      business_name: capitalizeWords(String(supplierFormData.business_name || '').trim()),
      rut: formatChileanRut(String(supplierFormData.rut || '').trim()),
      address: addresses[0]?.address || '',
      supplier_type: String(supplierFormData.supplier_type || '').trim(),
      status: String(supplierFormData.status || 'Activo').trim() || 'Activo',
      payment_conditions: Array.isArray(supplierFormData.payment_conditions)
        ? supplierFormData.payment_conditions
        : [],
      addresses,
      bank_accounts: bankAccounts,
    };

    setIsSavingSupplier(true);
    setSupplierFormError('');
    try {
      let saved;
      if (isEditingSupplier && editingSupplierId) {
        saved = await suppliersAPI.update(editingSupplierId, payload);
      } else {
        saved = await suppliersAPI.create(payload);
      }
      const existing = contactsBySupplierId.get(Number(saved.id)) || [];
      await syncSupplierContacts(saved.id, supplierFormData.contacts || [], existing);
      setShowSupplierForm(false);
      setEditingSupplierId(null);
      setSupplierFormData({ ...EMPTY_SUPPLIER_FORM });
      await Promise.all([loadSuppliers(), loadContacts()]);
    } catch (error) {
      setSupplierFormError(error?.message || 'No se pudo guardar el proveedor.');
    } finally {
      setIsSavingSupplier(false);
    }
  };

  const handleBulkImportSuppliers = async (rows) => {
    setIsImportingSuppliers(true);
    setBulkImportResult(null);
    try {
      const data = await suppliersAPI.bulkImport(
        (Array.isArray(rows) ? rows : []).map((row) => ({
          ...row,
          rut: formatChileanRut(row.rut || ''),
          name: normalizeProperCaseText(row.name || ''),
          business_name: normalizeProperCaseText(row.business_name || ''),
        }))
      );
      setBulkImportResult(data);
      await Promise.all([loadSuppliers(), loadContacts()]);
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setBulkImportNotice(
          `Importación exitosa: ${created} proveedor(es) creados` +
            (skipped ? `, ${skipped} omitidos` : '') +
            '.'
        );
      }
      return data;
    } catch (error) {
      const fail = {
        created: 0,
        skipped: 0,
        errors: 1,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingSuppliers(false);
    }
  };

  const handleBulkDeleteSuppliers = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1
        ? '¿Eliminar este proveedor?'
        : `¿Eliminar ${ids.length} proveedores?`
    );
    if (!ok) return false;
    await suppliersAPI.bulkDelete(ids);
    await Promise.all([loadSuppliers(), loadContacts()]);
    return true;
  };

  const openCreateContact = () => {
    setIsEditingContact(false);
    setEditingContactId(null);
    setContactFormError('');
    setContactFormData({ ...EMPTY_CONTACT_FORM });
    setShowContactForm(true);
  };

  const openEditContact = (contact) => {
    if (!contact) return;
    setIsEditingContact(true);
    setEditingContactId(contact.id);
    setContactFormError('');
    setContactFormData({
      full_name: contact.full_name || '',
      role: contact.role || '',
      email: contact.email || '',
      phone: contact.phone || '',
      supplier_id: contact.supplier_id ? String(contact.supplier_id) : '',
      status: contact.status || 'Activo',
    });
    setShowContactForm(true);
  };

  const handleSaveContact = async () => {
    const fullName = capitalizeWords(String(contactFormData.full_name || '').trim());
    const supplierId = Number(contactFormData.supplier_id || 0);
    if (!fullName) {
      setContactFormError('El nombre del contacto es obligatorio.');
      return;
    }
    if (!supplierId) {
      setContactFormError('Debes seleccionar un proveedor.');
      return;
    }
    const payload = {
      full_name: fullName,
      role: capitalizeWords(String(contactFormData.role || '').trim()),
      email: String(contactFormData.email || '').trim(),
      phone: String(contactFormData.phone || '').trim(),
      status: String(contactFormData.status || 'Activo').trim() || 'Activo',
      supplier_id: supplierId,
    };
    setIsSavingContact(true);
    setContactFormError('');
    try {
      if (isEditingContact && editingContactId) {
        await supplierContactsAPI.update(editingContactId, payload);
      } else {
        await supplierContactsAPI.create(payload);
      }
      setShowContactForm(false);
      setEditingContactId(null);
      setContactFormData({ ...EMPTY_CONTACT_FORM });
      await Promise.all([loadContacts(), loadSuppliers()]);
    } catch (error) {
      setContactFormError(error?.message || 'No se pudo guardar el contacto.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleBulkImportContacts = async (rows) => {
    setIsImportingContacts(true);
    setContactBulkImportResult(null);
    try {
      const data = await supplierContactsAPI.bulkImport(
        (Array.isArray(rows) ? rows : []).map((row) => ({
          ...row,
          full_name: capitalizeWords(String(row.full_name || '').trim()),
          role: capitalizeWords(String(row.role || '').trim()),
        }))
      );
      setContactBulkImportResult(data);
      await Promise.all([loadContacts(), loadSuppliers()]);
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setBulkImportNotice(
          `Importación exitosa: ${created} contacto(s) creados` +
            (skipped ? `, ${skipped} omitidos` : '') +
            '.'
        );
      }
      return data;
    } catch (error) {
      const fail = {
        created: 0,
        skipped: 0,
        errors: 1,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setContactBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingContacts(false);
    }
  };

  const handleBulkDeleteContacts = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1
        ? '¿Eliminar este contacto?'
        : `¿Eliminar ${ids.length} contactos?`
    );
    if (!ok) return false;
    await supplierContactsAPI.bulkDelete(ids);
    await Promise.all([loadContacts(), loadSuppliers()]);
    return true;
  };

  const contactFormSections = [
    {
      key: 'basic',
      title: 'Contacto',
      fields: [
        { key: 'full_name', label: 'Nombre' },
        { key: 'role', label: 'Cargo' },
        { key: 'email', label: 'Correo', type: 'email' },
        { key: 'phone', label: 'Teléfono' },
        {
          key: 'supplier_id',
          label: 'Proveedor',
          type: 'select',
          options: supplierSelectOptions,
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ],
        },
      ],
    },
  ];

  const tabs = [
    {
      key: 'suppliers',
      label: 'Proveedores',
      icon: <Building2 size={16} />,
      content: (
        <SuppliersTable
          rows={suppliers}
          loading={suppliersLoading}
          error={suppliersError}
          isDarkMode={isDarkMode}
          onAdd={openCreateSupplier}
          onBulkImport={() => {
            setBulkImportResult(null);
            setShowBulkImportModal(true);
          }}
          onBulkImportTemplate={downloadSuppliersExcelTemplate}
          onRowNameClick={setDetailSupplier}
          onBulkDelete={handleBulkDeleteSuppliers}
        />
      ),
    },
    {
      key: 'contacts',
      label: 'Contactos',
      icon: <Users size={16} />,
      content: (
        <SupplierContactsTable
          rows={contacts}
          loading={contactsLoading}
          error={contactsError}
          isDarkMode={isDarkMode}
          onAdd={openCreateContact}
          onBulkImport={() => {
            setContactBulkImportResult(null);
            setShowContactBulkImportModal(true);
          }}
          onBulkImportTemplate={downloadSupplierContactsExcelTemplate}
          onEdit={openEditContact}
          onBulkDelete={handleBulkDeleteContacts}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Proveedores
        </Heading>
        <p style={{ margin: 0, maxWidth: 680, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Maestro de proveedores de adquisiciones. Las métricas de compras se conectarán cuando exista
          el módulo de órdenes de compra.
        </p>
      </div>

      {bulkImportNotice ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${ui.border}`,
            background: isDarkMode ? 'rgba(22, 163, 74, 0.12)' : '#f0fdf4',
            color: ui.text,
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <span>{bulkImportNotice}</span>
          <button
            type="button"
            onClick={() => setBulkImportNotice('')}
            style={{
              border: 'none',
              background: 'transparent',
              color: ui.muted,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            Cerrar
          </button>
        </div>
      ) : null}

      <TabNavigation tabs={tabs} defaultTab="suppliers" isDarkMode={isDarkMode} />

      <SuppliersBulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => {
          if (isImportingSuppliers) return;
          setShowBulkImportModal(false);
          setBulkImportResult(null);
        }}
        onImport={handleBulkImportSuppliers}
        isImporting={isImportingSuppliers}
        isDarkMode={isDarkMode}
        result={bulkImportResult}
        supplierTypeOptions={supplierTypeOptions}
      />

      <SupplierContactsBulkImportModal
        isOpen={showContactBulkImportModal}
        onClose={() => {
          if (isImportingContacts) return;
          setShowContactBulkImportModal(false);
          setContactBulkImportResult(null);
        }}
        onImport={handleBulkImportContacts}
        isImporting={isImportingContacts}
        isDarkMode={isDarkMode}
        result={contactBulkImportResult}
      />

      <SuppliersForm
        isOpen={showSupplierForm}
        onClose={() => !isSavingSupplier && setShowSupplierForm(false)}
        onSave={handleSaveSupplier}
        isSaving={isSavingSupplier}
        isDarkMode={isDarkMode}
        isEditing={isEditingSupplier}
        formData={supplierFormData}
        onChange={(key, value) => setSupplierFormData((prev) => ({ ...prev, [key]: value }))}
        formError={supplierFormError}
        paymentConditionOptions={paymentConditionOptions}
        supplierTypeOptions={supplierTypeOptions}
        currencyOptions={currencyOptions}
      />

      <FormDrawer
        isOpen={showContactForm}
        onClose={() => !isSavingContact && setShowContactForm(false)}
        onSave={handleSaveContact}
        isSaving={isSavingContact}
        isDarkMode={isDarkMode}
        title={isEditingContact ? 'Editar contacto' : 'Nuevo contacto'}
        subtitle="Contacto de proveedor"
        sections={contactFormSections}
        formData={contactFormData}
        onChange={(key, value) => setContactFormData((prev) => ({ ...prev, [key]: value }))}
        formError={contactFormError}
        width={520}
      />

      <SupplierDetailModal
        isOpen={Boolean(detailSupplier)}
        onClose={() => setDetailSupplier(null)}
        supplier={detailSupplier}
        contacts={detailContacts}
        isDarkMode={isDarkMode}
        onEdit={openEditSupplier}
      />
    </div>
  );
};

export default PaintSuppliersWorkspace;
